import { useEffect, useState, type ReactNode } from 'react'
import { SeedCost } from '@/components/gg/SeedChip'

/**
 * The desktop list + detail (Brian's desk-split.js, Oct 22): the list stays on
 * the left, compact, and the chosen item opens on the right with Dismiss and
 * Build. J/K or the arrows move, B builds, X dismisses. After either action the
 * next one down opens (not the top of the list).
 */
export function SplitQueue<T extends { id: string }>({ items, card, detail, onBuild, onDismiss, cost, after }: {
  items: T[]
  card: (item: T, selected: boolean, select: () => void) => ReactNode
  detail: (item: T) => ReactNode
  /** Return false when the action didn't happen (the selection stays). */
  onBuild: (item: T) => boolean
  onDismiss: (item: T) => void
  cost: number
  after?: ReactNode
}) {
  const [sel, setSel] = useState<string | null>(items[0]?.id ?? null)
  const ids = items.map((i) => i.id)
  const cur = items.find((i) => i.id === sel) ?? items[0] ?? null

  useEffect(() => { if (!sel || !ids.includes(sel)) setSel(ids[0] ?? null) }, [ids.join('|')]) // eslint-disable-line react-hooks/exhaustive-deps

  const act = (fn: (i: T) => boolean | void) => {
    if (!cur) return
    const i = ids.indexOf(cur.id)
    const next = ids[i + 1] ?? ids[i - 1] ?? null
    if (fn(cur) === false) return
    setSel(next)
  }

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement
      if (e.metaKey || e.ctrlKey || e.altKey || /INPUT|TEXTAREA|SELECT/.test(t.tagName || '') || t.isContentEditable) return
      if (document.querySelector('.gga-sheet')) return
      const i = cur ? ids.indexOf(cur.id) : -1
      const key = e.key.toLowerCase()
      if (key === 'j' || e.key === 'ArrowDown') setSel(ids[Math.min(ids.length - 1, i + 1)] ?? null)
      else if (key === 'k' || e.key === 'ArrowUp') setSel(ids[Math.max(0, i - 1)] ?? null)
      else if (key === 'b' && cur) act(onBuild)
      else if (key === 'x' && cur) act(onDismiss)
      else return
      e.preventDefault()
      requestAnimationFrame(() => document.querySelector('.gga-list .gga-card.sel')?.scrollIntoView({ block: 'nearest' }))
    }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  })

  return (
    <div className="gga-split">
      <div className="gga-list">
        {items.map((i) => card(i, cur?.id === i.id, () => setSel(i.id)))}
        {after}
      </div>
      <aside className="gga-detail" aria-live="polite">
        {cur ? (
          <div className="gga-pane">
            {detail(cur)}
            <div className="gga-acts">
              <button type="button" className="gga-btn" onClick={() => act(onDismiss)}>Dismiss</button>
              <button type="button" className="gga-btn gga-btn--b" onClick={() => act(onBuild)}>Build<SeedCost n={cost} /></button>
              <span className="keys">J/K move · B build · X dismiss</span>
            </div>
          </div>
        ) : <div className="empty">Nothing waiting. When something lands it opens here.</div>}
      </aside>
    </div>
  )
}
